<template>
  <section
    class="settings-card weekly-settings-card"
    data-test="weekly-settings"
  >
    <div class="settings-card-header">
      <div class="settings-card-heading">
        <h3>Weekly Timesheets With External Approval</h3>
        <p>
          Lets your tradies fill in their own week in the app and send it to a
          supervisor outside your company for approval. The supervisor gets an
          emailed link and a code, and needs no account. You finalise approved
          sheets before the hours reach payroll.
        </p>
      </div>
      <b-form-checkbox
        v-model="enabled"
        switch
        class="weekly-switch"
        :disabled="saving || !loaded"
        aria-label="Weekly timesheets with external approval"
        data-test="weekly-switch"
        @change="save"
      >
        <span class="weekly-switch__label" data-test="weekly-state">
          {{ enabled ? 'On' : 'Off' }}
        </span>
      </b-form-checkbox>
    </div>

    <div class="settings-form">
      <p class="weekly-hint" data-test="weekly-hint">
        <template v-if="enabled">
          Tradies see a Weekly tab on their Timesheet History once they are on a
          project that has a supervisor. Add each project's supervisors on its
          <router-link :to="{ name: 'projects.list-page' }">
            Supervisors tab
          </router-link>
          . Turning this off hides the Weekly tab in the app. Links already
          emailed keep working until the supervisor decides.
        </template>
        <template v-else>
          Off. Your tradies cannot start a weekly timesheet.
        </template>
      </p>
    </div>
  </section>
</template>

<script>
export default {
  name: 'WeeklyTimesheetSettingsCard',

  data() {
    return {
      enabled: false,
      loaded: false,
      saving: false,
    }
  },

  computed: {
    settings() {
      return this.$store.getters['timesheet/timesheetSettings']
    },
  },

  watch: {
    settings: {
      immediate: true,
      handler(s) {
        if (!s) return
        this.enabled = !!s.weekly_timesheet_external_enabled
        this.loaded = true
      },
    },
  },

  methods: {
    async save(value) {
      this.saving = true
      try {
        await this.$store.dispatch('timesheet/updateTimesheetSettings', {
          weekly_timesheet_external_enabled: !!value,
        })
        this.$bvToast.toast(
          value
            ? 'Weekly timesheets are on for your company.'
            : 'Weekly timesheets are off for your company.',
          { title: 'Saved', variant: 'success', solid: true },
        )
      } catch (e) {
        // Put the switch back to what the server still has.
        this.enabled = !value
        this.$bvToast.toast('Could not change this setting. Try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.weekly-settings-card {
  margin-top: 20px;
}

.settings-card {
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03),
    0 8px 24px rgba(15, 23, 42, 0.04);
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #eef2f6;

  h3 {
    margin: 0 0 4px;
    font-size: 17px;
    font-weight: 700;
    color: #111827;
  }

  p {
    margin: 0;
    max-width: 640px;
    color: #6b7280;
    font-size: 14px;
    line-height: 1.5;
  }
}

.weekly-switch {
  flex: 0 0 auto;
  min-height: 22px;
  padding-left: 46px;
  margin-top: 2px;
  white-space: nowrap;

  &__label {
    font-weight: 600;
    color: #111827;
    min-width: 2ch;
    display: inline-block;
  }

  // Same switch as "Show archived" on Plant & Equipment: 40x22 track, grey
  // when off, brand blue when on, label to the right.
  ::v-deep .custom-control-label {
    min-height: 22px;
    padding-top: 2px;
    padding-left: 0;
    line-height: 18px;
    cursor: pointer;
  }

  ::v-deep .custom-control-label::before {
    top: 0;
    left: -46px;
    width: 40px !important;
    height: 22px !important;
    border-color: #94a3b8;
    background: #94a3b8;
  }

  ::v-deep .custom-control-label::after {
    top: 3px;
    left: -43px;
    width: 16px !important;
    height: 16px !important;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #006cff;
    background: #006cff;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
    left: -25px;
    transform: none;
  }

  ::v-deep .custom-control-input:disabled ~ .custom-control-label {
    cursor: default;
    opacity: 0.6;
  }
}

.settings-form {
  padding: 16px 24px 20px;
}

.weekly-hint {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  max-width: 720px;
}

@media (max-width: 575.98px) {
  .settings-card-header {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
